/**
 * A value chip (missions vision, "Create/edit dialog"): one small quiet control per value in a
 * single row (kind, status, priority, points, parent), showing the value with its icon; a click
 * opens the menu of choices. The chip's accessible name says what it sets and to what.
 */
import { ChevronDown } from '@orgus/ui/icons';
import type { ReactNode } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger, Pressable, cn } from '@orgus/ui';

export interface ChipOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export function ValueChip({ label, value, options, onChange, display, icon, disabled, className }: {
  /** What it sets: "Priority". */
  label: string;
  value: string;
  options: readonly ChipOption[];
  onChange: (v: string) => void;
  /** How the chosen value reads on the chip, when not its option label. */
  display?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Pressable
          aria-label={`${label}: ${current?.label ?? 'None'}`}
          className={cn(
            'control-shell flex h-control-sm w-auto min-w-0 max-w-64 items-center gap-2 px-2 text-ui text-fg-muted hover:bg-hover hover:text-fg aria-expanded:bg-hover',
            className,
          )}
        >
          {icon ?? current?.icon}
          <span className="min-w-0 truncate">{display ?? current?.label ?? label}</span>
          <ChevronDown className="size-3 shrink-0 text-fg-subtle" aria-hidden />
        </Pressable>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 max-w-96 overflow-y-auto" aria-label={label}>
        <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
          {options.map((o) => (
            <DropdownMenuRadioItem key={o.value || 'none'} value={o.value} className="gap-2">
              {o.icon}
              <span className="min-w-0 truncate">{o.label}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
